import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Check, Copy, Download } from "lucide-react";
import { qrAgenzieApi, type QrAgenziaStato } from "@/api/qrAgenzie";
import { copyText } from "@/lib/clipboard";
import { buttonVariants } from "@/components/ui/button";

/**
 * Pagina «QR Self Service Clienti» del titolare (Alfonso 2026-09-29: voce di menù apposita,
 * solo per il titolare e solo col QR acceso; prima era una scheda nel profilo). A sinistra
 * il cartello così come si stampa, a destra le azioni.
 */
export function QrSelfServiceClienti() {
  const [stato, setStato] = useState<QrAgenziaStato | null | undefined>(undefined);
  const [cartello, setCartello] = useState<string | null>(null);
  const [copiato, setCopiato] = useState(false);
  // M7: un doppio click lanciava due render Chromium sul backend: pulsanti PDF
  // disabilitati finché uno scaricamento è in corso.
  const [scaricando, setScaricando] = useState(false);

  useEffect(() => {
    let vivo = true;
    qrAgenzieApi.stato()
      .then((s) => {
        if (!vivo) return;
        setStato(s);
        if (s.attivo) {
          qrAgenzieApi.cartelloHtml().then((h) => { if (vivo) setCartello(h); }).catch(() => {});
        }
      })
      .catch(() => { if (vivo) setStato(null); });
    return () => { vivo = false; };
  }, []);

  if (stato === undefined) return null;
  // QR spento o non raggiungibile: la voce di menù non c'è, e l'indirizzo scritto a mano
  // riporta alla dashboard invece di mostrare una pagina vuota.
  if (!stato?.attivo) return <Navigate to="/agenzia/dashboard" replace />;

  async function copia() {
    if (!stato?.link) return;
    if (await copyText(stato.link)) {
      setCopiato(true);
      setTimeout(() => setCopiato(false), 2500);
    }
  }

  async function scarica(formato: "A4" | "A5") {
    if (scaricando) return;
    setScaricando(true);
    try {
      await qrAgenzieApi.scaricaCartelloPdf(formato);
    } catch {
      // silenzioso, come prima nella scheda del profilo
    } finally {
      setScaricando(false);
    }
  }

  return (
    <div className="max-w-5xl">
      <h1 className="text-xl font-semibold text-text mb-6">QR Self Service Clienti</h1>
      <section className="bg-card p-4 sm:p-6 rounded-lg border border-border flex flex-col sm:flex-row gap-6 sm:gap-8 items-start">
        {/* Il cartello vero (stesso HTML del PDF), in un riquadro senza script: `srcdoc`
            perché il portale vieta di incorniciare i suoi URL (X-Frame-Options DENY).
            Le misure del cartello sono in vw/vh: nel riquadro 1:√2 si scala da solo. */}
        <div className="w-full sm:w-64 shrink-0 aspect-[1/1.4142] rounded-md border border-border overflow-hidden bg-sidebar">
          {cartello && (
            <iframe
              title="Anteprima del cartello"
              srcDoc={cartello}
              sandbox=""
              className="w-full h-full block pointer-events-none"
            />
          )}
        </div>

        <div className="flex-1 space-y-5 min-w-0 w-full">
          <p className="text-base text-text leading-relaxed">
            Le pratiche fatte dal QR compaiono tra le Pratiche, e per ciascuna ricevi un avviso.
          </p>

          <div className="grid sm:flex sm:flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void scarica("A4")}
              disabled={scaricando}
              className={buttonVariants({ variant: "default", className: "h-11" })}
            >
              <Download size={16} aria-hidden="true" />
              Cartello A4 (PDF)
            </button>
            <button
              type="button"
              onClick={() => void scarica("A5")}
              disabled={scaricando}
              className={buttonVariants({ variant: "outline", className: "h-11" })}
            >
              <Download size={16} aria-hidden="true" />
              Cartello A5 (PDF)
            </button>
            <button
              type="button"
              onClick={() => void copia()}
              className={buttonVariants({ variant: "outline", className: "h-11" })}
            >
              {copiato ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
              {copiato ? "Copiato ✓" : "Copia link"}
            </button>
          </div>

          {stato.pratiche_mese != null && (
            <p className="text-base text-muted-foreground">
              Pratiche dal QR questo mese: <span className="font-medium text-text">{stato.pratiche_mese}</span>
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
